// FlightActions — botones de check-in y boarding pass para cada vuelo.
// 1) "Hacer check-in": copia PNR + abre la web de la aerolínea
// 2) "Subir boarding pass": archivo → IA extrae → guarda asociado al vuelo
//    Soporta MÚLTIPLES pasajeros (1 vuelo, N boarding passes).
import { useState, useEffect, useRef, useMemo } from "react";
import { Plane, Upload, Loader2, Copy, Check, ExternalLink, Trash2, Eye, Plus } from "lucide-react";
import { getAirlineInfo, checkinStatus, hoursUntil } from "../data/airlines";
import { api } from "../auth/AuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtCountdown(hrs) {
  if (hrs == null) return "";
  if (hrs < 0) return "ya despegó";
  if (hrs < 1) return `en ${Math.round(hrs * 60)} min`;
  if (hrs < 48) return `en ${Math.round(hrs)} h`;
  return `en ${Math.round(hrs / 24)} días`;
}

export default function FlightActions({ flight, tripId }) {
  const [busyUpload, setBusyUpload] = useState(false);
  const [doc, setDoc] = useState(null); // {passes: [...]}
  const [previewIdx, setPreviewIdx] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const airline = getAirlineInfo(flight.airline);
  const status = checkinStatus(flight);
  const hrs = hoursUntil(flight.depart);

  // Normaliza la respuesta (soporta legacy single-pass)
  const passes = useMemo(() => {
    if (!doc) return [];
    if (Array.isArray(doc.passes)) return doc.passes;
    if (doc.data_b64) return [{ id: "legacy", mime: doc.mime, data_b64: doc.data_b64, extracted: doc.extracted || {}, label: (doc.extracted || {}).passengerName, uploaded_at: doc.uploaded_at }];
    return [];
  }, [doc]);

  // Carga inicial
  useEffect(() => {
    if (!tripId || !flight.id) return;
    api(`/api/trips/${tripId}/flights/${flight.id}/boarding-pass`)
      .then(setDoc)
      .catch(() => setDoc(null));
  }, [tripId, flight.id]);

  const doCheckIn = async () => {
    const pnr = flight.bookingRef || "";
    if (pnr) {
      try {
        await navigator.clipboard.writeText(pnr);
        setCopied(true);
        toast.success(`PNR ${pnr} copiado. Pégalo en la página de ${airline.name}.`);
        setTimeout(() => setCopied(false), 2500);
      } catch {
        toast.info(`Anota tu PNR: ${pnr}`);
      }
    } else {
      toast.info(`No tienes PNR guardado. Edita el vuelo y agrégalo.`);
    }
    window.open(airline.checkinUrl, "_blank", "noopener,noreferrer");
  };

  const onFileChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    for (const file of files) {
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name}: muy grande (máx 8 MB)`);
        continue;
      }
      setBusyUpload(true);
      try {
        const tok = (() => { try { return localStorage.getItem("session_token"); } catch { return null; } })();
        const extractFD = new FormData();
        extractFD.append("entity", "boarding_pass");
        extractFD.append("file", file);
        const extractR = await fetch(`${API}/api/ai/extract/file`, {
          method: "POST",
          credentials: "include",
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
          body: extractFD,
        });
        const extractData = extractR.ok ? await extractR.json() : { items: [] };
        const item = extractData.items?.[0] || {};

        const uploadFD = new FormData();
        uploadFD.append("file", file);
        uploadFD.append("extracted", JSON.stringify(item));
        if (item.passengerName) uploadFD.append("label", item.passengerName);
        const upR = await fetch(`${API}/api/trips/${tripId}/flights/${flight.id}/boarding-pass`, {
          method: "POST",
          credentials: "include",
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
          body: uploadFD,
        });
        if (!upR.ok) throw new Error(`Upload failed (${upR.status})`);
        toast.success(`Boarding pass guardado ✨${item.passengerName ? ` · ${item.passengerName}` : ""}${item.seat ? ` · Asiento ${item.seat}` : ""}`);
      } catch (err) {
        toast.error(`No pude procesar ${file.name}.`);
      }
    }
    // Refresh once at the end
    try {
      const bp = await api(`/api/trips/${tripId}/flights/${flight.id}/boarding-pass`);
      setDoc(bp);
    } catch {}
    setBusyUpload(false);
  };

  const deletePass = async (passId) => {
    if (!window.confirm("¿Eliminar este boarding pass?")) return;
    await api(`/api/trips/${tripId}/flights/${flight.id}/boarding-pass?pass_id=${encodeURIComponent(passId)}`, { method: "DELETE" });
    try {
      const bp = await api(`/api/trips/${tripId}/flights/${flight.id}/boarding-pass`);
      setDoc(bp);
    } catch {
      setDoc(null);
    }
    setPreviewIdx(null);
  };

  const previewing = previewIdx != null ? passes[previewIdx] : null;

  return (
    <div className="mt-4 pt-3 border-t border-[hsl(var(--line))]" data-testid={`flight-actions-${flight.id}`}>
      {/* Lista de boarding passes guardados */}
      {passes.length > 0 && (
        <div className="mb-3 space-y-2" data-testid={`bp-list-${flight.id}`}>
          {passes.map((bp, idx) => {
            const ext = bp.extracted || {};
            const name = bp.label || ext.passengerName || `Pasajero ${idx + 1}`;
            return (
              <div
                key={bp.id || idx}
                className="rounded-xl bg-[hsl(140_35%_95%)] border border-[hsl(140_40%_80%)] p-3 flex items-center gap-2"
                data-testid={`bp-item-${flight.id}-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(140_50%_30%)]">🎫 {name}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5 text-xs">
                    {ext.seat && <span className="font-bold">Asiento {ext.seat}</span>}
                    {ext.gate && <><span className="text-[hsl(var(--muted))]">·</span><span>Gate {ext.gate}</span></>}
                    {ext.terminal && <><span className="text-[hsl(var(--muted))]">·</span><span>{ext.terminal}</span></>}
                    {ext.boardingTime && <><span className="text-[hsl(var(--muted))]">·</span><span>Embarque {ext.boardingTime}</span></>}
                    {ext.zone && <><span className="text-[hsl(var(--muted))]">·</span><span>Zona {ext.zone}</span></>}
                  </div>
                </div>
                <button onClick={() => setPreviewIdx(idx)} className="text-[hsl(140_50%_30%)] active:scale-95 p-1" aria-label="Ver" data-testid={`view-bp-${flight.id}-${idx}`}>
                  <Eye size={18} />
                </button>
                <button onClick={() => deletePass(bp.id || "legacy")} className="text-[hsl(var(--muted))] active:scale-95 p-1" aria-label="Eliminar" data-testid={`delete-bp-${flight.id}-${idx}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={doCheckIn}
          className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-[hsl(var(--ink))] text-white text-xs font-semibold active:scale-[0.97] transition-transform"
          data-testid={`checkin-btn-${flight.id}`}
        >
          <div className="flex items-center gap-1.5">
            {copied ? <Check size={14} /> : <ExternalLink size={14} />}
            <span>{passes.length > 0 ? "Re-abrir check-in" : "Hacer check-in"}</span>
          </div>
          <span className="text-[10px] opacity-80 font-normal">
            {airline.name}
            {status === "open" && " · ABIERTO"}
            {status === "soon" && ` · abre ${fmtCountdown(hrs - airline.opensHoursBefore)}`}
            {status === "later" && ` · en ${Math.round((hrs - airline.opensHoursBefore) / 24)} días`}
            {status === "closed" && " · ya despegó"}
          </span>
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busyUpload || !tripId}
          className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-[hsl(var(--accent))] text-white text-xs font-semibold active:scale-[0.97] transition-transform disabled:opacity-50"
          data-testid={`upload-bp-btn-${flight.id}`}
        >
          <div className="flex items-center gap-1.5">
            {busyUpload ? <Loader2 size={14} className="animate-spin" /> : (passes.length > 0 ? <Plus size={14} /> : <Upload size={14} />)}
            <span>{passes.length > 0 ? `Agregar otro (${passes.length})` : "Subir boarding pass"}</span>
          </div>
          <span className="text-[10px] opacity-80 font-normal">
            {busyUpload ? "Leyendo con IA…" : (passes.length > 0 ? "Para el otro pasajero" : "PDF o foto · varios pasajeros")}
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,image/*"
          multiple
          onChange={onFileChosen}
          className="hidden"
          data-testid={`bp-file-input-${flight.id}`}
        />
      </div>

      {flight.bookingRef && (
        <p className="text-[10px] text-[hsl(var(--muted))] text-center mt-2">
          PNR <span className="font-mono font-semibold">{flight.bookingRef}</span> se copiará al abrir check-in
        </p>
      )}

      {/* Preview modal */}
      {previewing && (
        <div
          className="fixed inset-0 z-[1200] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewIdx(null)}
          data-testid={`bp-preview-${flight.id}`}
        >
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-4 py-3 border-b flex items-center justify-between">
              <p className="font-semibold text-sm">
                {previewing.label || previewing.extracted?.passengerName || `Pasajero ${previewIdx + 1}`} · {flight.airline}
              </p>
              <button onClick={() => setPreviewIdx(null)} className="text-[hsl(var(--muted))]" aria-label="Cerrar">✕</button>
            </div>
            {previewing.mime?.startsWith("image/") ? (
              <img src={`data:${previewing.mime};base64,${previewing.data_b64}`} alt="Boarding pass" className="w-full" />
            ) : (
              <iframe
                src={`data:${previewing.mime};base64,${previewing.data_b64}`}
                title="Boarding pass"
                className="w-full"
                style={{ height: "75vh", border: 0 }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
